import { Project, Script, Episode, Shot, ShotVersion, Resource, ScriptAnalysis, AssetRef, AssetSearchTask, AssetStyleTest, PromptSkill, ScriptSkill, SkillVariable, Preprocessor, VoicePreset, PresetAudioReview, User, ProjectMember, GenerationTemplate, LLMProvider, LLMRoleConfigMap, TokenUsageSummary, VideoModel, VideoUsageSummary, ImageModel, ImageUsageSummary, ProjectServiceUsageSummary, CreditRecharge, RechargeRequest, CreditConsumption, ProjectCreditsSummary, ServicePricing, ServiceUsageSummary, VoiceExtractionTask, VoiceSwapTask, VoiceSwapMatchedSegment, SubtitleEraseTask, Announcement, Notification, UnreadSummary, ServiceLog, AnalysisMode, ResourceReviewItem, ProjectAsset, ShowcaseItem, DemoTemplate, ShotComment, AssetComment, AssetCommentPending, EpisodeTranslationSummary, EpisodeTranslationDetail, JobRole, AdminPermission } from '../types';
import { Language, LanguageSkill, LanguageSkillVariable } from '../types/language';
import i18n from '../locales';
import {
  ApiError,
  apiErrorFromResponse as parseApiErrorFromResponse,
} from './errors';
import {
  getScriptFileValidationMessageKey,
  validateScriptUploadFile,
} from '../utils/scriptFileValidation';

export * from './characterStyle';
export * from './directorAnnotations';
export { ApiError, isApiError } from './errors';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

export function getToken(): string {
  return localStorage.getItem('token') || '';
}

function localizeError(message: string): string {
  const key = `errors.${message}`;
  const localized = i18n.t(key, { ns: 'common', defaultValue: '' });
  return localized || message;
}

async function assertValidScriptUploadFile(file: File): Promise<void> {
  const validation = await validateScriptUploadFile(file);
  if (!validation.ok) {
    throw new Error(i18n.t(getScriptFileValidationMessageKey(validation.reason), { ns: 'projectDetail' }));
  }
}

export async function apiErrorFromResponse(response: Response, fallback: string): Promise<ApiError> {
  return parseApiErrorFromResponse(response, fallback, localizeError);
}

export async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
    throw new Error(localizeError('未登录'));
  }

  if (!response.ok) {
    throw await apiErrorFromResponse(response, 'Request failed');
  }

  return response.json();
}

// Auth
export const login = async (username: string, password: string): Promise<{ token: string; user: User }> => {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json().catch(() => ({ error: '登录失败' }));
  if (!response.ok) throw new Error(localizeError(data.error || '登录失败'));
  return data;
};

export const getMe = (): Promise<User> =>
  request('/auth/me');

export const changePassword = (oldPassword: string, newPassword: string): Promise<void> =>
  request('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ oldPassword, newPassword }),
  });

export const changeMyPhone = (phone: string): Promise<User> =>
  request('/auth/change-phone', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });

// Admin user management
export const adminListUsers = (
  page = 1,
  pageSize = 20,
  username = '',
  phone = '',
  userId = '',
): Promise<{ items: User[]; total: number }> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (username) params.set('username', username);
  if (phone) params.set('phone', phone);
  if (userId) params.set('id', userId);
  return request(`/admin/users?${params.toString()}`);
};

export const adminCreateUser = (username: string, password: string, phone: string, accountGroupId: string, demoTemplateId: number, jobRole: JobRole = 'generator', note = ''): Promise<User> =>
  request('/admin/users', {
    method: 'POST',
    body: JSON.stringify({ username, password, phone, accountGroupId, demoTemplateId, jobRole, note }),
  });

export const adminCreateAdmin = (username: string, password: string, phone: string, permissions: AdminPermission[], note = ''): Promise<User> =>
  request('/admin/admins', {
    method: 'POST',
    body: JSON.stringify({ username, password, phone, permissions, note }),
  });

export const adminUpdateUserPermissions = (id: string, permissions: AdminPermission[]): Promise<void> =>
  request(`/admin/users/${id}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  });

export const adminUpdateUserPassword = (id: string, password: string): Promise<void> =>
  request(`/admin/users/${id}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });

export const adminUpdateUserPhone = (id: string, phone: string): Promise<void> =>
  request(`/admin/users/${id}/phone`, {
    method: 'PUT',
    body: JSON.stringify({ phone }),
  });

export const adminUpdateUserJobRole = (id: string, jobRole: JobRole): Promise<void> =>
  request(`/admin/users/${id}/job-role`, {
    method: 'PUT',
    body: JSON.stringify({ jobRole }),
  });

export const adminUpdateUserNote = (id: string, note: string): Promise<void> =>
  request(`/admin/users/${id}/note`, {
    method: 'PUT',
    body: JSON.stringify({ note }),
  });

export const adminUpdateUserRole = (id: string, role: string): Promise<void> =>
  request(`/admin/users/${id}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });

export const adminDeleteUser = (id: string): Promise<void> =>
  request(`/admin/users/${id}`, { method: 'DELETE' });

export const banUser = (id: string): Promise<{ message: string; creditsCleared: boolean }> =>
  request(`/admin/users/${id}/ban`, { method: 'POST' });

export const unbanUser = (id: string): Promise<{ message: string }> =>
  request(`/admin/users/${id}/unban`, { method: 'POST' });

// Demo templates (admin)
export const listDemoTemplates = (): Promise<DemoTemplate[]> =>
  request('/admin/demo-templates');

export const createDemoTemplate = (sourceProjectId: string, nameTemplate: string): Promise<DemoTemplate> =>
  request('/admin/demo-templates', { method: 'POST', body: JSON.stringify({ sourceProjectId, nameTemplate }) });

export const updateDemoTemplate = (id: number, sourceProjectId: string, nameTemplate: string): Promise<DemoTemplate> =>
  request(`/admin/demo-templates/${id}`, { method: 'PUT', body: JSON.stringify({ sourceProjectId, nameTemplate }) });

export const deleteDemoTemplate = (id: number): Promise<{ success: boolean }> =>
  request(`/admin/demo-templates/${id}`, { method: 'DELETE' });

// Projects
export interface CreateProjectParams {
  name: string;
  projectPrompt?: string;
  aspectRatio?: string;
  skillId?: string;
  skillVariableValues?: Record<string, string>;
  scriptSkillId?: string;
  scriptSkillValues?: Record<string, string>;
  enableScriptNormalization?: boolean;
  autoGenerateShots?: boolean;
  jimengModel?: string;
  imagePlatform?: string;
  imageModel?: string;
  baseImagePlatform?: string;
  baseImageModel?: string;
  videoPlatform?: string;
  videoModel?: string;
  llmRoleOverrides?: Partial<LLMRoleConfigMap>;
  autoGenerateCharImages?: boolean;
  autoGenerateSceneImages?: boolean;
  autoGeneratePropImages?: boolean;
  charFixedPrompt?: string;
  charProjectRefImages?: string;
  charStatePromptPrefix?: string;
  sceneStatePromptPrefix?: string;
  propStatePromptPrefix?: string;
  charStateImageRatio?: string;
  sceneStateImageRatio?: string;
  propStateImageRatio?: string;
  baseConfigCompleted?: boolean;
  videoPrefixPrompt?: string;
  videoExtraPrompt?: string;
  sceneFixedPrompt?: string;
  sceneProjectRefImages?: string;
  propFixedPrompt?: string;
  propProjectRefImages?: string;
  useAdvancedAnalysis?: boolean;
  analysisMode?: AnalysisMode;
  autoStartAnalysis?: boolean;
  enableRefImageSearch?: boolean;
  autoSearchCharRef?: boolean;
  autoSearchSceneRef?: boolean;
  autoSearchPropRef?: boolean;
  enableVoiceInsertion?: boolean;
  voicePrompt?: string;
  voicePresetId?: string;
  disableBGM?: boolean;
  generationTemplateId?: string;
  projectType?: string;
  outputLanguage?: string;
  languageSkillId?: string;
  languageSkillValues?: Record<string, string>;
  refLanguages?: string[];
}

export const createProject = (params: CreateProjectParams): Promise<Project> =>
  request('/projects', {
    method: 'POST',
    body: JSON.stringify({ aspectRatio: '16:9', autoGenerateShots: true, jimengModel: 'jimeng-5.0', ...params }),
  });

export const listProjects = (page = 0, search = ''): Promise<{ projects: Project[]; total: number }> =>
  request(`/projects?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ''}`);

export const listFavoriteProjects = (page = 0, search = ''): Promise<{ projects: Project[]; total: number }> =>
  request(`/projects/favorites?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ''}`);

export const favoriteProject = (id: string): Promise<{ ok: boolean }> =>
  request(`/projects/${id}/favorite`, { method: 'POST' });

export const unfavoriteProject = (id: string): Promise<{ ok: boolean }> =>
  request(`/projects/${id}/favorite`, { method: 'DELETE' });

export const getProject = (id: string): Promise<Project> =>
  request(`/projects/${id}`);

export const getRegistryUrls = (projectId: string): Promise<Record<string, unknown>> =>
  request(`/projects/${projectId}/registry-urls`);

export const updateProject = (id: string, params: CreateProjectParams): Promise<Project> =>
  request(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });

export const deleteProject = (id: string): Promise<void> =>
  request(`/projects/${id}`, { method: 'DELETE' });

export interface CloneProjectPayload {
  name: string;
  projectType: 'real_person' | 'animation';
  skillId?: string;
  scriptSkillId?: string;
  languageSkillId?: string;
  outputLanguage?: string;
  enableScriptNormalization?: boolean;
  disableBGM?: boolean;
  scriptSkillValues?: Record<string, string>;
  languageSkillValues?: Record<string, string>;
  keepAssetImages: boolean;
  keepAssetImagePrompts: boolean;
  keepShotPrompts: boolean;
}

export const cloneProject = (sourceId: string, payload: CloneProjectPayload): Promise<Project> =>
  request(`/projects/${sourceId}/clone`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Project members
export const listProjectMembers = (projectId: string): Promise<ProjectMember[]> =>
  request(`/projects/${projectId}/members`);

export const addProjectMember = (projectId: string, username: string): Promise<void> =>
  request(`/projects/${projectId}/members`, {
    method: 'POST',
    body: JSON.stringify({ username }),
  });

export const removeProjectMember = (projectId: string, userId: string): Promise<void> =>
  request(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' });

// Scripts
export const uploadScript = async (projectId: string, file: File): Promise<Script> => {
  await assertValidScriptUploadFile(file);
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/script`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '上传失败' }));
    throw new Error(localizeError(err.error || '上传失败'));
  }
  return res.json();
};

export const getScript = (projectId: string): Promise<Script> =>
  request(`/projects/${projectId}/script`);

export const retryScript = (projectId: string): Promise<void> =>
  request(`/projects/${projectId}/script/retry`, { method: 'POST' });

export const appendScript = async (projectId: string, file: File): Promise<{ message: string; appendCount: number; startIndex: number; endIndex: number }> => {
  await assertValidScriptUploadFile(file);
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/script/append`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '追加失败' }));
    throw new Error(localizeError(err.error || '追加失败'));
  }
  return res.json();
};

export const retryAnalysis = (projectId: string): Promise<void> =>
  request(`/projects/${projectId}/analysis/retry`, { method: 'POST' });

export const cancelAnalysis = (projectId: string): Promise<void> =>
  request(`/projects/${projectId}/analysis/cancel`, { method: 'POST' });

// Episodes
export const listEpisodes = (projectId: string): Promise<Episode[]> =>
  request(`/projects/${projectId}/episodes`);

export const getEpisode = (id: string): Promise<Episode> =>
  request(`/episodes/${id}`);

export const getEpisodeContent = (id: string, version?: 'annotated' | 'normalized'): Promise<{ content: string }> =>
  request(`/episodes/${id}/content${version === 'annotated' ? '?annotated=true' : version === 'normalized' ? '?normalized=true' : ''}`);

export const getRefTranslationLanguages = (): Promise<{ languages: string[] }> =>
  request('/ref-translation/languages');

export const getEpisodeTranslations = (id: string): Promise<{ translations: EpisodeTranslationSummary[] }> =>
  request(`/episodes/${id}/translations`);

export const getEpisodeTranslation = (id: string, language: string): Promise<EpisodeTranslationDetail> =>
  request(`/episodes/${id}/translation?language=${encodeURIComponent(language)}`);

export const startEpisodeTranslation = (id: string, language: string): Promise<EpisodeTranslationSummary> =>
  request(`/episodes/${id}/translate`, { method: 'POST', body: JSON.stringify({ language }) });

export const updateEpisode = async (id: string, content: string): Promise<Episode> => {
  const formData = new FormData();
  formData.append('content', content);
  const token = getToken();
  const res = await fetch(`${API_BASE}/episodes/${id}`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '更新失败' }));
    throw new Error(localizeError(err.error || '更新失败'));
  }
  return res.json();
};

export const generateShots = (id: string): Promise<void> =>
  request(`/episodes/${id}/generate-shots`, { method: 'POST' });

export const generatePrompts = (id: string): Promise<void> =>
  request(`/episodes/${id}/generate-prompts`, { method: 'POST' });

export const regenerateAllPrompts = (projectId: string, onlyFailed = false): Promise<{ triggered: number }> =>
  request(`/projects/${projectId}/regenerate-all-prompts${onlyFailed ? '?onlyFailed=true' : ''}`, { method: 'POST' });

export const normalizeAllEpisodes = (projectId: string): Promise<{ triggered: number; message: string }> =>
  request(`/projects/${projectId}/normalize-all-episodes`, { method: 'POST' });

export const matchEpisodeAssetStates = (episodeId: string): Promise<{ matched: number }> =>
  request(`/episodes/${episodeId}/match-asset-states`, { method: 'POST' });

// Shots
export const listShots = (episodeId: string): Promise<Shot[]> =>
  request(`/episodes/${episodeId}/shots`);

export const createShot = (episodeId: string, afterIndex: number, data?: { duration?: number; prompt?: string }): Promise<Shot> =>
  request(`/episodes/${episodeId}/shots`, { method: 'POST', body: JSON.stringify({ afterIndex, ...data }) });

export const getShot = (id: string): Promise<Shot> =>
  request(`/shots/${id}`);

export const updateShot = (id: string, data: {
  duration: number;
  description: string;
  remark: string;
  prompt: string;
}): Promise<Shot> =>
  request(`/shots/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

// Shot comments
export const listShotComments = (shotId: string): Promise<ShotComment[]> =>
  request(`/shots/${shotId}/comments`);

export const createShotComment = (shotId: string, content: string, imageUrls: string[] = []): Promise<ShotComment> =>
  request(`/shots/${shotId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content, images: imageUrls }),
  });

export const resolveShotComment = (commentId: string): Promise<ShotComment> =>
  request(`/comments/${commentId}/resolve`, { method: 'PUT' });

// Asset comments (角色/场景/道具)
export const listAssetComments = (projectId: string, category: string, name: string): Promise<AssetComment[]> =>
  request(`/projects/${projectId}/asset-comments?category=${encodeURIComponent(category)}&name=${encodeURIComponent(name)}`, { cache: 'no-store' });

export const createAssetComment = (projectId: string, category: string, name: string, content: string, imageUrls: string[] = []): Promise<AssetComment> =>
  request(`/projects/${projectId}/asset-comments`, {
    method: 'POST',
    body: JSON.stringify({ category, name, content, images: imageUrls }),
  });

export const getAssetCommentPending = (projectId: string): Promise<AssetCommentPending[]> =>
  request(`/projects/${projectId}/asset-comments/pending`);

export const resolveAssetComment = (commentId: string): Promise<AssetComment> =>
  request(`/asset-comments/${commentId}/resolve`, { method: 'PUT' });

export const updateShotSelectedVersion = (shotId: string, versionId: string): Promise<{ status: string }> =>
  request(`/shots/${shotId}/selected-version`, {
    method: 'PUT',
    body: JSON.stringify({ versionId }),
  });

export const updateShotSecondaryVersions = (shotId: string, versionIds: string[]): Promise<{ status: string }> =>
  request(`/shots/${shotId}/secondary-versions`, {
    method: 'PUT',
    body: JSON.stringify({ versionIds }),
  });

export const generateVideo = (id: string): Promise<{ id: string; status: string; videoTaskId: string; versionId: string }> =>
  request(`/shots/${id}/generate-video`, { method: 'POST' });

export const rematchShotAssets = (shotId: string): Promise<{ assetRefs: AssetRef[]; prompt: string }> =>
  request(`/shots/${shotId}/rematch-assets`, { method: 'POST' });

export const deleteShot = (shotId: string): Promise<{ message: string }> =>
  request(`/shots/${shotId}`, { method: 'DELETE' });

export const duplicateShot = (shotId: string): Promise<Shot> =>
  request(`/shots/${shotId}/duplicate`, { method: 'POST' });

export const cancelVideo = (shotId: string, versionId: string): Promise<{ status: string }> =>
  request(`/shots/${shotId}/cancel-video`, {
    method: 'POST',
    body: JSON.stringify({ versionId }),
  });

export const listShotVersions = (shotId: string): Promise<ShotVersion[]> =>
  request(`/shots/${shotId}/versions`);

export const listAllEpisodeVersions = (episodeId: string): Promise<Record<string, ShotVersion[]>> =>
  request(`/episodes/${episodeId}/all-versions`);

export const updateShotAssetRef = (shotId: string, imgIndex: number, state: string, imageUrl: string): Promise<{ assetRefs: AssetRef[] }> =>
  request(`/shots/${shotId}/asset-refs`, {
    method: 'PUT',
    body: JSON.stringify({ imgIndex, state, imageUrl }),
  });

export const addShotAssetRef = (shotId: string, type: string | undefined, name: string, state: string, imageUrl: string, allStates: { state: string; imageUrl: string; episodes?: number[] }[], source?: string): Promise<{ assetRefs: AssetRef[]; prompt?: string }> =>
  request(`/shots/${shotId}/asset-refs`, {
    method: 'POST',
    body: JSON.stringify({ type, name, state, imageUrl, allStates, ...(source ? { source } : {}) }),
  });

export const removeShotAssetRef = (shotId: string, imgIndex: number): Promise<{ assetRefs: AssetRef[]; prompt: string }> =>
  request(`/shots/${shotId}/asset-refs`, {
    method: 'DELETE',
    body: JSON.stringify({ imgIndex }),
  });

export const getShotVersion = (versionId: string): Promise<ShotVersion> =>
  request(`/shots/versions/${versionId}`);

// Resources
export const uploadResource = async (
  shotId: string, type: string, file: File,
  options?: { containsHuman?: boolean; resourceName?: string }
): Promise<Resource & { reviewStatus?: string; prompt?: string }> => {
  const formData = new FormData();
  formData.append('type', type);
  formData.append('file', file);
  if (options?.containsHuman) {
    formData.append('containsHuman', 'true');
    if (options.resourceName) formData.append('resourceName', options.resourceName);
  }
  const token = getToken();
  const res = await fetch(`${API_BASE}/shots/${shotId}/resources`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '上传失败' }));
    throw new Error(localizeError(err.error || '上传失败'));
  }
  return res.json();
};

export const listResources = (shotId: string): Promise<Resource[]> =>
  request(`/shots/${shotId}/resources`);

export const createRefResource = (shotId: string, url: string, fileName: string, type: string, containsHuman?: boolean): Promise<{ resource: Resource; prompt?: string }> =>
  request(`/shots/${shotId}/resources/ref`, {
    method: 'POST',
    body: JSON.stringify({ url, fileName, type, containsHuman: containsHuman ?? false }),
  });

export const deleteResource = (id: string): Promise<{ message: string; prompt?: string }> =>
  request(`/resources/${id}`, { method: 'DELETE' });

// 项目级素材库（「其他素材」tab）
export const getProjectAssets = (projectId: string): Promise<{ assets: ProjectAsset[] }> =>
  request(`/projects/${projectId}/assets`);

export const deleteProjectAsset = (id: string): Promise<void> =>
  request(`/project-assets/${id}`, { method: 'DELETE' });

// 历史记录视频截取
export interface ClipCaptureResult {
  url: string;
  destination: 'review' | 'library';
  reviewStatus?: string;
}

export const uploadVersionFrame = async (
  shotId: string, versionId: string, blob: Blob,
  options: { containsHuman: boolean; name: string }
): Promise<ClipCaptureResult> => {
  const formData = new FormData();
  formData.append('file', blob, `${options.name}.jpg`);
  if (options.containsHuman) formData.append('containsHuman', 'true');
  formData.append('name', options.name);
  const token = getToken();
  const res = await fetch(`${API_BASE}/shots/${shotId}/versions/${versionId}/upload-frame`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '截取失败' }));
    throw new Error(localizeError(err.error || '截取失败'));
  }
  return res.json();
};

export const captureVersionClip = (
  shotId: string, versionId: string,
  body: { startTime: number; endTime: number; containsHuman: boolean; name: string; source?: 'erased' }
): Promise<ClipCaptureResult> =>
  request(`/shots/${shotId}/versions/${versionId}/capture-clip`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Script Analysis
export const getScriptAnalysis = (projectId: string): Promise<ScriptAnalysis> =>
  request(`/projects/${projectId}/analysis`);

export const addAnalysisElement = (
  projectId: string,
  elementType: string,
  name: string,
  state: string,
  description?: string,
  episodes?: number[],
): Promise<void> =>
  request(`/projects/${projectId}/analysis/element`, {
    method: 'POST',
    body: JSON.stringify({
      elementType,
      name,
      state,
      description,
      ...(episodes !== undefined ? { episodes } : {}),
    }),
  });

// PUT accepts partial updates: undefined = leave unchanged; explicit value (including empty) overwrites.
// This matches the backend's *string / *[]int pointer semantics.
export interface UpdateAnalysisElementPatch {
  imagePrompt?: string;
  episodes?: number[];
  promptIncludesPrefix?: boolean;
}

export const updateAnalysisElement = (
  projectId: string,
  elementType: string,
  elementName: string,
  patch: UpdateAnalysisElementPatch | string,
): Promise<void> => {
  // Back-compat: callers that still pass a bare string mean "update imagePrompt only".
  const body = typeof patch === 'string' ? { imagePrompt: patch } : patch;
  return request(`/projects/${projectId}/analysis/element`, {
    method: 'PUT',
    body: JSON.stringify({ elementType, elementName, ...body }),
  });
};

export const generateAnalysisImage = (projectId: string, elementType: string, elementName: string, prompt: string, baseImageUrl: string | undefined, skipPrefix: boolean | undefined, platform: string | undefined, model: string | undefined, requestId: string): Promise<{ taskId: string }> =>
  request(`/projects/${projectId}/analysis/generate-image`, {
    method: 'POST',
    body: JSON.stringify({ elementType, elementName, prompt, requestId, ...(baseImageUrl ? { baseImageUrl } : {}), ...(skipPrefix ? { skipPrefix: true } : {}), ...(platform && model ? { platform, model } : {}) }),
  });

export const uploadAnalysisUserImage = async (projectId: string, elementType: string, elementName: string, file: File): Promise<{ imageUrl: string }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('elementName', elementName);
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/analysis/upload-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(localizeError(err.error || 'Upload failed'));
  }
  return res.json();
};

export const uploadAnalysisRefImage = async (projectId: string, elementType: string, elementName: string, file: File): Promise<{ refImageUrls: string[] }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('elementName', elementName);
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/analysis/ref-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(localizeError(err.error || 'Upload failed'));
  }
  return res.json();
};

export const importAnalysisRefUrl = (projectId: string, elementType: string, elementName: string, url: string): Promise<{ refImageUrls: string[] }> =>
  request(`/projects/${projectId}/analysis/import-ref-url`, {
    method: 'POST',
    body: JSON.stringify({ elementType, elementName, url }),
  });

export const importAnalysisImageUrl = (projectId: string, elementType: string, elementName: string, url: string): Promise<{ imageUrl: string }> =>
  request(`/projects/${projectId}/analysis/import-image-url`, {
    method: 'POST',
    body: JSON.stringify({ elementType, elementName, url }),
  });

export const clearAnalysisRefImage = (projectId: string, elementType: string, elementName: string, url?: string): Promise<void> =>
  request(`/projects/${projectId}/analysis/ref-image`, {
    method: 'DELETE',
    body: JSON.stringify({ elementType, elementName, url }),
  });

export const updateAnalysisSelectedImage = (projectId: string, elementType: string, elementName: string, selectedImageUrl: string, invalidateReview?: boolean): Promise<void> =>
  request(`/projects/${projectId}/analysis/selected-image`, {
    method: 'PUT',
    body: JSON.stringify({ elementType, elementName, selectedImageUrl, invalidateReview: !!invalidateReview }),
  });

export interface ImageTaskStatusResponse {
  status: number;
  imageUrls: string[];
  error: string;
  failureStage?: 'persist';
}

export interface ActiveImageTaskResponseItem {
  taskId: string;
  elementType: string;
  elementName: string;
}

export interface FailedImageTaskResponseItem extends ActiveImageTaskResponseItem {
  error?: string;
  failureStage?: 'persist';
}

export interface ActiveImageTasksResponse {
  tasks: ActiveImageTaskResponseItem[];
  failed?: FailedImageTaskResponseItem[];
  tasksBase?: ActiveImageTaskResponseItem[];
  failedBase?: FailedImageTaskResponseItem[];
}

export const getImageTaskStatus = (taskId: string): Promise<ImageTaskStatusResponse> =>
  request(`/analysis/tasks/${taskId}`);

export const getActiveImageTasks = (projectId: string): Promise<ActiveImageTasksResponse> =>
  request(`/projects/${projectId}/analysis/active-tasks`);

export const regenerateImagePrompts = (projectId: string): Promise<void> =>
  request(`/projects/${projectId}/analysis/regenerate-prompts`, { method: 'POST' });

export const generateAnalysisImagePrompts = (projectId: string): Promise<void> =>
  request(`/projects/${projectId}/analysis/generate-image-prompts`, { method: 'POST' });

// External (资深) analysis availability — controlled by admin global config.
// fail-closed: any error → caller should treat as unavailable.
export interface ExternalAnalysisAvailability {
  available: boolean;
}

export const getExternalAnalysisAvailability = (): Promise<ExternalAnalysisAvailability> =>
  request('/external-analysis/availability');

// Admin-only: read/write the global UseExternalAnalysis kill-switch.
export interface AdminExternalAnalysisFlag {
  useExternalAnalysis: boolean;
}

export const getAdminExternalAnalysisFlag = (): Promise<AdminExternalAnalysisFlag> =>
  request('/admin/global-config/use-external-analysis');

export const patchAdminExternalAnalysisFlag = (
  useExternalAnalysis: boolean,
): Promise<AdminExternalAnalysisFlag> =>
  request('/admin/global-config/use-external-analysis', {
    method: 'PATCH',
    body: JSON.stringify({ useExternalAnalysis }),
  });

// Base image APIs
export const uploadAssetBaseRefImage = async (projectId: string, elementType: string, elementName: string, file: File): Promise<{ baseRefImages: string[] }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('elementName', elementName);
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/analysis/base-ref-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw await apiErrorFromResponse(res, 'Upload failed');
  return res.json();
};

// 上传用户自定义候选基础图（复用 upload-image 接口，target=base 落到 baseImageUrls）
export const uploadAnalysisBaseImage = async (projectId: string, elementType: string, elementName: string, file: File): Promise<{ imageUrl: string }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('elementName', elementName);
  formData.append('target', 'base');
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/analysis/upload-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw await apiErrorFromResponse(res, 'Upload failed');
  return res.json();
};

export const deleteAssetBaseRefImage = (projectId: string, elementType: string, elementName: string, url: string): Promise<{ baseRefImages: string[] }> =>
  request(`/projects/${projectId}/analysis/base-ref-image`, { method: 'DELETE', body: JSON.stringify({ elementType, elementName, url }) });

export const generateBaseImage = (projectId: string, elementType: string, elementName: string, prompt: string, requestId: string, excludeProjectRefUrls?: string[], platform?: string, model?: string): Promise<{ taskId: string }> =>
  request(`/projects/${projectId}/analysis/generate-base-image`, { method: 'POST', body: JSON.stringify({ elementType, elementName, prompt, requestId, ...(excludeProjectRefUrls?.length ? { excludeProjectRefUrls } : {}), ...(platform && model ? { platform, model } : {}) }) });

export const selectBaseImage = (projectId: string, elementType: string, elementName: string, selectedBaseImageUrl: string): Promise<{ selectedBaseImageUrl: string }> =>
  request(`/projects/${projectId}/analysis/selected-base-image`, { method: 'PUT', body: JSON.stringify({ elementType, elementName, selectedBaseImageUrl }) });

export const uploadAssetConfigRefImage = async (projectId: string, elementType: string, file: File): Promise<{ projectRefImages: string[] }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/asset-config-ref-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Upload failed' })); throw new Error(localizeError(e.error)); }
  return res.json();
};

export const deleteAssetConfigRefImage = (projectId: string, elementType: string, url: string): Promise<{ projectRefImages: string[] }> =>
  request(`/projects/${projectId}/asset-config-ref-image`, { method: 'DELETE', body: JSON.stringify({ elementType, url }) });

// Skills
export const listSkills = (): Promise<PromptSkill[]> =>
  request('/skills');

export const createSkill = (data: { name: string; description: string; systemPrompt: string; variables?: SkillVariable[]; isDefault: boolean; preprocessors?: Preprocessor[] }): Promise<PromptSkill> =>
  request('/skills', { method: 'POST', body: JSON.stringify(data) });

export const updateSkill = (id: string, data: { name: string; description: string; systemPrompt: string; variables?: SkillVariable[]; isDefault: boolean; preprocessors?: Preprocessor[] }): Promise<PromptSkill> =>
  request(`/skills/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteSkill = (id: string): Promise<void> =>
  request(`/skills/${id}`, { method: 'DELETE' });

// Skill 项目选择用（可见性过滤）
export const listSkillsForProject = (currentSkillId?: string): Promise<PromptSkill[]> =>
  request(`/skills?for=project${currentSkillId ? `&currentSkillId=${currentSkillId}` : ''}`);

// Skill 审核工作流
export const submitSkill = (id: string): Promise<PromptSkill> =>
  request(`/skills/${id}/submit`, { method: 'POST' });

export const withdrawSkill = (id: string): Promise<PromptSkill> =>
  request(`/skills/${id}/withdraw`, { method: 'POST' });

export const approveSkill = (id: string): Promise<PromptSkill> =>
  request(`/admin/skills/${id}/approve`, { method: 'POST' });

export const rejectSkill = (id: string, reviewNote: string): Promise<PromptSkill> =>
  request(`/admin/skills/${id}/reject`, { method: 'POST', body: JSON.stringify({ reviewNote }) });

export const publishSkill = (id: string): Promise<PromptSkill> =>
  request(`/admin/skills/${id}/publish`, { method: 'POST' });

export const unpublishSkill = (id: string): Promise<PromptSkill> =>
  request(`/admin/skills/${id}/unpublish`, { method: 'POST' });

// Script Skills (剧本改写技能)
export const listScriptSkills = (): Promise<ScriptSkill[]> =>
  request('/script-skills');

export const createScriptSkill = (data: { name: string; description: string; systemPrompt: string; variables?: SkillVariable[]; isDefault: boolean }): Promise<ScriptSkill> =>
  request('/script-skills', { method: 'POST', body: JSON.stringify(data) });

export const updateScriptSkill = (id: string, data: { name: string; description: string; systemPrompt: string; variables?: SkillVariable[]; isDefault: boolean }): Promise<ScriptSkill> =>
  request(`/script-skills/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteScriptSkill = (id: string): Promise<void> =>
  request(`/script-skills/${id}`, { method: 'DELETE' });

// ScriptSkill 项目选择用（可见性过滤）
export const listScriptSkillsForProject = (currentSkillId?: string): Promise<ScriptSkill[]> =>
  request(`/script-skills?for=project${currentSkillId ? `&currentSkillId=${currentSkillId}` : ''}`);

// ScriptSkill 审核工作流
export const submitScriptSkill = (id: string): Promise<ScriptSkill> =>
  request(`/script-skills/${id}/submit`, { method: 'POST' });

export const withdrawScriptSkill = (id: string): Promise<ScriptSkill> =>
  request(`/script-skills/${id}/withdraw`, { method: 'POST' });

export const approveScriptSkill = (id: string): Promise<ScriptSkill> =>
  request(`/admin/script-skills/${id}/approve`, { method: 'POST' });

export const rejectScriptSkill = (id: string, reviewNote: string): Promise<ScriptSkill> =>
  request(`/admin/script-skills/${id}/reject`, { method: 'POST', body: JSON.stringify({ reviewNote }) });

export const publishScriptSkill = (id: string): Promise<ScriptSkill> =>
  request(`/admin/script-skills/${id}/publish`, { method: 'POST' });

export const unpublishScriptSkill = (id: string): Promise<ScriptSkill> =>
  request(`/admin/script-skills/${id}/unpublish`, { method: 'POST' });

// Languages (语言列表)
export const listLanguages = (): Promise<Language[]> =>
  request('/languages');

// Admin languages
export const adminListLanguages = (): Promise<Language[]> =>
  request('/admin/languages');

export const createLanguage = (data: { code: string; name: string; enabled?: boolean; displayOrder?: number; isDefault?: boolean }): Promise<Language> =>
  request('/admin/languages', { method: 'POST', body: JSON.stringify(data) });

export const updateLanguage = (id: string, data: { code?: string; name?: string; enabled?: boolean; displayOrder?: number; isDefault?: boolean }): Promise<Language> =>
  request(`/admin/languages/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteLanguage = (id: string): Promise<void> =>
  request(`/admin/languages/${id}`, { method: 'DELETE' });

// Language Skills (语言改写技能)
export const listLanguageSkills = (params: { languageCode?: string; status?: string } = {}): Promise<LanguageSkill[]> => {
  const qs = new URLSearchParams();
  if (params.languageCode) qs.set('language_code', params.languageCode);
  if (params.status) qs.set('status', params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request(`/language-skills${suffix}`);
};

export const getLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/language-skills/${id}`);

export const createLanguageSkill = (data: { name: string; description: string; languageCode: string; systemPrompt: string; variables?: LanguageSkillVariable[]; isDefault: boolean }): Promise<LanguageSkill> =>
  request('/language-skills', { method: 'POST', body: JSON.stringify(data) });

export const updateLanguageSkill = (id: string, data: { name: string; description: string; languageCode: string; systemPrompt: string; variables?: LanguageSkillVariable[]; isDefault: boolean }): Promise<LanguageSkill> =>
  request(`/language-skills/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteLanguageSkill = (id: string): Promise<void> =>
  request(`/language-skills/${id}`, { method: 'DELETE' });

// LanguageSkill 审核工作流
export const submitLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/language-skills/${id}/submit`, { method: 'POST' });

export const withdrawLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/language-skills/${id}/withdraw`, { method: 'POST' });

export const approveLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/admin/language-skills/${id}/approve`, { method: 'POST' });

export const rejectLanguageSkill = (id: string, reviewComment: string): Promise<LanguageSkill> =>
  request(`/admin/language-skills/${id}/reject`, { method: 'POST', body: JSON.stringify({ reviewComment }) });

export const publishLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/admin/language-skills/${id}/publish`, { method: 'POST' });

export const unpublishLanguageSkill = (id: string): Promise<LanguageSkill> =>
  request(`/admin/language-skills/${id}/unpublish`, { method: 'POST' });

// Shot 语言改写触发
export const applyLanguageToShot = (shotId: string): Promise<{ status: string }> =>
  request(`/shots/${shotId}/apply-language`, { method: 'POST' });

// Normalize episode
export const normalizeEpisode = (episodeId: string): Promise<{ message: string }> =>
  request(`/episodes/${episodeId}/normalize`, { method: 'POST' });

// Resolve pronouns (代词标注)
export const resolvePronouns = (episodeId: string): Promise<{ message: string }> =>
  request(`/episodes/${episodeId}/resolve-pronouns`, { method: 'POST' });

// Voice Presets
export const listVoicePresets = (): Promise<VoicePreset[]> =>
  request('/voice-presets');

// 项目选择用（可见性过滤：本人 + 已上架/审核中 + 当前选中）
export const listVoicePresetsForProject = (currentId?: string): Promise<VoicePreset[]> =>
  request(`/voice-presets?for=project${currentId ? `&currentId=${currentId}` : ''}`);

export const createVoicePreset = (data: { name: string; prompt: string; isDefault: boolean }): Promise<VoicePreset> =>
  request('/voice-presets', { method: 'POST', body: JSON.stringify(data) });

export const updateVoicePreset = (id: string, data: { name: string; prompt: string; isDefault: boolean }): Promise<VoicePreset> =>
  request(`/voice-presets/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteVoicePreset = (id: string): Promise<void> =>
  request(`/voice-presets/${id}`, { method: 'DELETE' });

// VoicePreset 审核工作流
export const submitVoicePreset = (id: string): Promise<VoicePreset> =>
  request(`/voice-presets/${id}/submit`, { method: 'POST' });

export const withdrawVoicePreset = (id: string): Promise<VoicePreset> =>
  request(`/voice-presets/${id}/withdraw`, { method: 'POST' });

export const approveVoicePreset = (id: string): Promise<VoicePreset> =>
  request(`/admin/voice-presets/${id}/approve`, { method: 'POST' });

export const rejectVoicePreset = (id: string, reviewNote: string): Promise<VoicePreset> =>
  request(`/admin/voice-presets/${id}/reject`, { method: 'POST', body: JSON.stringify({ reviewNote }) });

export const publishVoicePreset = (id: string): Promise<VoicePreset> =>
  request(`/admin/voice-presets/${id}/publish`, { method: 'POST' });

export const unpublishVoicePreset = (id: string): Promise<VoicePreset> =>
  request(`/admin/voice-presets/${id}/unpublish`, { method: 'POST' });

// 音色预设音频（admin）：直接 multipart 传给后端，后端存 TOS 并回 {audioUrl}。
// 走裸 fetch（不走 request）——`request` 会强设 Content-Type: application/json，
// 会覆盖浏览器为 FormData 自动生成的 multipart boundary，照现有分镜资源上传的写法。
export const uploadPresetAudio = async (id: string, file: File): Promise<{ audioUrl: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/admin/voice-presets/${id}/audio`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '上传失败' }));
    throw new Error(localizeError(err.error || '上传失败'));
  }
  return res.json();
};

export const listPresetAudioReviews = (id: string): Promise<PresetAudioReview[]> =>
  request(`/voice-presets/${id}/audio-reviews`);

export const resubmitPresetAudioReview = (id: string, platform: string): Promise<{ message: string }> =>
  request(`/admin/voice-presets/${id}/audio-reviews/${platform}/resubmit`, { method: 'POST' });

// Analysis Config (admin only)
export interface AnalysisConfig {
  extractRules: string;
  consolidateRules: string;
  supplementRules: string;
  pronounResolveSystemPrompt: string;
  defaultExtractRules: string;
  defaultConsolidateRules: string;
  defaultSupplementRules: string;
  defaultPronounResolveSystemPrompt: string;
  assetJSONSpec: string;
  promptFormatSpec: string;
  // 高级分析
  advancedExtractRules: string;
  advancedMergeRules: string;
  advancedValidateRules: string;
  defaultAdvancedExtractRules: string;
  defaultAdvancedMergeRules: string;
  defaultAdvancedValidateRules: string;
  advancedFormatSpec: string;
  // 深度思考开关
  thinkingExtract: boolean;
  thinkingConsolidate: boolean;
  thinkingSupplement: boolean;
  thinkingAdvExtract: boolean;
  thinkingAdvMerge: boolean;
  thinkingAdvValidate: boolean;
  thinkingPrompt: boolean;
  thinkingStateMatch: boolean;
  // 资产状态匹配
  assetStateMatchRules: string;
  defaultAssetStateMatchRules: string;
  assetStateMatchFormatSpec: string;
  // Director annotation → storyboard mapping
  directorMarkMappingSystemPrompt: string;
  directorMarkMappingJSONSpec: string;
  defaultDirectorMarkMappingSystemPrompt: string;
  defaultDirectorMarkMappingJSONSpec: string;
  // 资产搜索关键词规则
  characterKeywordRules: string;
  sceneKeywordRules: string;
  propKeywordRules: string;
  defaultCharacterKeywordRules: string;
  defaultSceneKeywordRules: string;
  defaultPropKeywordRules: string;
  assetKeywordFormatSpec: string;
  thinkingAssetKeyword: boolean;
  // Character casting/styling tag rules
  castingTagsRules: string;
  stylingTagsRules: string;
  defaultCastingTagsRules: string;
  defaultStylingTagsRules: string;
  castingTagsFormatSpec: string;
  stylingTagsFormatSpec: string;
  // 选角 AI 精排 rules + 可编辑 spec
  castingRankRules: string;
  defaultCastingRankRules: string;
  castingRankFormatSpec: string;
  defaultCastingRankFormatSpec: string;
  // Analytics 报告提示词
  analyticsSystemPrompt: string;
  analyticsUserPromptTemplate: string;
  defaultAnalyticsSystemPrompt: string;
  defaultAnalyticsUserPromptTemplate: string;
  refTranslationPrompt: string;
  refTranslationLangs: string;
  defaultRefTranslationPrompt: string;
  defaultRefTranslationLangs: string;
}

export const getAnalysisConfig = (): Promise<AnalysisConfig> =>
  request('/admin/analysis-config');

export const updateAnalysisConfig = (data: {
  extractRules: string; consolidateRules: string; supplementRules: string;
  pronounResolveSystemPrompt?: string;
  advancedExtractRules?: string; advancedMergeRules?: string; advancedValidateRules?: string;
  thinkingExtract?: boolean; thinkingConsolidate?: boolean; thinkingSupplement?: boolean;
  thinkingAdvExtract?: boolean; thinkingAdvMerge?: boolean; thinkingAdvValidate?: boolean;
  thinkingPrompt?: boolean; thinkingStateMatch?: boolean;
  thinkingAssetKeyword?: boolean;
  assetStateMatchRules?: string;
  directorMarkMappingSystemPrompt?: string;
  directorMarkMappingJSONSpec?: string;
  characterKeywordRules?: string; sceneKeywordRules?: string; propKeywordRules?: string;
  castingTagsRules?: string; stylingTagsRules?: string;
  castingRankRules?: string; castingRankFormatSpec?: string;
  analyticsSystemPrompt?: string; analyticsUserPromptTemplate?: string;
  refTranslationPrompt?: string;
  refTranslationLangs?: string;
}): Promise<void> =>
  request('/admin/analysis-config', { method: 'PUT', body: JSON.stringify(data) });

// Asset Search Tasks
export const getAssetSearchTasks = (projectId: string): Promise<AssetSearchTask[]> =>
  request(`/projects/${projectId}/asset-search-tasks`);

export const triggerAssetSearch = (projectId: string, category?: number): Promise<{ message: string }> =>
  request(`/projects/${projectId}/asset-search-tasks/trigger`, {
    method: 'POST',
    ...(category ? { body: JSON.stringify({ category }) } : {}),
  });

export const manualTriggerAssetSearch = (projectId: string, data: { category?: number; taskIds?: number[] }): Promise<{ message: string }> =>
  request(`/projects/${projectId}/asset-search-tasks/search`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const retryAssetSearchTask = (projectId: string, taskId: number): Promise<{ message: string }> =>
  request(`/projects/${projectId}/asset-search-tasks/retry`, {
    method: 'POST',
    body: JSON.stringify({ taskId }),
  });

export const generateAssetKeywords = (
  projectId: string,
  data: { category: number; assetName: string; assetState: string }
): Promise<{ tasks: { id: number; keywords: string; status: string }[] }> =>
  request(`/projects/${projectId}/assets/keywords/generate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const selectSearchRefImages = (projectId: string, elementType: string, elementName: string, state: string, imageUrls: string[]): Promise<void> =>
  request(`/projects/${projectId}/asset-search-tasks/select`, {
    method: 'POST',
    body: JSON.stringify({ elementType, elementName, state, imageUrls }),
  });

export const selectBaseRefImages = (projectId: string, elementType: string, elementName: string, imageUrls: string[]): Promise<{ baseRefImages: string[] }> =>
  request(`/projects/${projectId}/analysis/base-ref-image/select`, {
    method: 'POST',
    body: JSON.stringify({ elementType, elementName, imageUrls }),
  });

// Generation Templates
export const listGenerationTemplates = (): Promise<GenerationTemplate[]> =>
  request('/generation-templates');

// 项目选择用（可见性过滤：本人 + 已上架/审核中 + 当前选中）
export const listGenerationTemplatesForProject = (currentId?: string): Promise<GenerationTemplate[]> =>
  request(`/generation-templates?for=project${currentId ? `&currentId=${currentId}` : ''}`);

export const getGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/generation-templates/${id}`);

export const createGenerationTemplate = (data: Partial<GenerationTemplate>): Promise<GenerationTemplate> =>
  request('/generation-templates', { method: 'POST', body: JSON.stringify(data) });

export const updateGenerationTemplate = (id: string, data: Partial<GenerationTemplate>): Promise<GenerationTemplate> =>
  request(`/generation-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteGenerationTemplate = (id: string): Promise<void> =>
  request(`/generation-templates/${id}`, { method: 'DELETE' });

export const uploadTemplateRefImage = async (templateId: string, elementType: string, file: File): Promise<{ projectRefImages: string[] }> => {
  const formData = new FormData();
  formData.append('elementType', elementType);
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/generation-templates/${templateId}/upload-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Upload failed' })); throw new Error(localizeError(e.error)); }
  return res.json();
};

export const deleteTemplateRefImage = (templateId: string, elementType: string, url: string): Promise<{ projectRefImages: string[] }> =>
  request(`/generation-templates/${templateId}/ref-image`, { method: 'DELETE', body: JSON.stringify({ elementType, url }) });

export const uploadTemplateCoverImage = async (templateId: string, file: File): Promise<{ coverImageUrl: string }> => {
  const formData = new FormData();
  formData.append('elementType', 'cover');
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/generation-templates/${templateId}/upload-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Upload failed' })); throw new Error(localizeError(e.error)); }
  return res.json();
};

export const deleteTemplateCoverImage = (templateId: string): Promise<{ coverImageUrl: string }> =>
  request(`/generation-templates/${templateId}/ref-image`, { method: 'DELETE', body: JSON.stringify({ elementType: 'cover', url: '' }) });

// GenerationTemplate 审核工作流
export const submitGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/generation-templates/${id}/submit`, { method: 'POST' });

export const withdrawGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/generation-templates/${id}/withdraw`, { method: 'POST' });

export const approveGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/admin/generation-templates/${id}/approve`, { method: 'POST' });

export const rejectGenerationTemplate = (id: string, reviewNote: string): Promise<GenerationTemplate> =>
  request(`/admin/generation-templates/${id}/reject`, { method: 'POST', body: JSON.stringify({ reviewNote }) });

export const publishGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/admin/generation-templates/${id}/publish`, { method: 'POST' });

export const unpublishGenerationTemplate = (id: string): Promise<GenerationTemplate> =>
  request(`/admin/generation-templates/${id}/unpublish`, { method: 'POST' });

// LLM Providers (admin only)
export const listLLMProviders = (): Promise<LLMProvider[]> =>
  request('/admin/llm-providers');

export const createLLMProvider = (data: { name: string; apiBase: string; apiKey: string; models: string; maxConcurrency?: number | null }): Promise<LLMProvider> =>
  request('/admin/llm-providers', { method: 'POST', body: JSON.stringify(data) });

export const updateLLMProvider = (id: string, data: { name: string; apiBase: string; apiKey?: string; models: string; maxConcurrency?: number | null }): Promise<LLMProvider> =>
  request(`/admin/llm-providers/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const toggleLLMProvider = (id: string, disabled: boolean): Promise<LLMProvider> =>
  request(`/admin/llm-providers/${id}/toggle`, { method: 'PUT', body: JSON.stringify({ disabled }) });

export const deleteLLMProvider = (id: string): Promise<void> =>
  request(`/admin/llm-providers/${id}`, { method: 'DELETE' });

// LLM Providers (public, for project settings)
export const listLLMProvidersPublic = (): Promise<LLMProvider[]> =>
  request('/llm-providers/list');

// LLM Role Config (admin only)
export const getLLMRoleConfig = (): Promise<LLMRoleConfigMap> =>
  request('/admin/llm-role-config');

export const updateLLMRoleConfig = (data: LLMRoleConfigMap): Promise<void> =>
  request('/admin/llm-role-config', { method: 'PUT', body: JSON.stringify(data) });

// Token Usage
export const getProjectTokenUsage = (projectId: string): Promise<TokenUsageSummary[]> =>
  request(`/projects/${projectId}/token-usage`);

// Video Usage
export const getProjectVideoUsage = (projectId: string): Promise<VideoUsageSummary[]> =>
  request(`/projects/${projectId}/video-usage`);

// Video Models
export const listActiveVideoModels = (): Promise<VideoModel[]> =>
  request('/video-models/active');

// Video Models (admin)
export const listVideoModels = (): Promise<VideoModel[]> =>
  request('/admin/video-models');

export const createVideoModel = (data: Partial<VideoModel>): Promise<VideoModel> =>
  request('/admin/video-models', { method: 'POST', body: JSON.stringify(data) });

export const updateVideoModel = (id: string, data: Partial<VideoModel>): Promise<VideoModel> =>
  request(`/admin/video-models/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteVideoModel = (id: string): Promise<void> =>
  request(`/admin/video-models/${id}`, { method: 'DELETE' });

// Image Usage
export const getProjectImageUsage = (projectId: string): Promise<ImageUsageSummary[]> =>
  request(`/projects/${projectId}/image-usage`);

export const getProjectServiceUsage = (projectId: string): Promise<ProjectServiceUsageSummary[]> =>
  request(`/projects/${projectId}/service-usage`);

// Image Models
export const listActiveImageModels = (): Promise<ImageModel[]> =>
  request('/image-models/active');

// Image Models (admin)
export const listImageModels = (): Promise<ImageModel[]> =>
  request('/admin/image-models');

export const createImageModel = (data: Partial<ImageModel>): Promise<ImageModel> =>
  request('/admin/image-models', { method: 'POST', body: JSON.stringify(data) });

export const updateImageModel = (id: string, data: Partial<ImageModel>): Promise<ImageModel> =>
  request(`/admin/image-models/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteImageModel = (id: string): Promise<void> =>
  request(`/admin/image-models/${id}`, { method: 'DELETE' });

// Share link
export const createShareLink = (episodeId: string, options?: { hours?: number; maxViews?: number }): Promise<{ code: string; expiresAt: string; maxViews: number }> =>
  request(`/episodes/${episodeId}/share`, { method: 'POST', body: JSON.stringify(options || {}) });

export interface SharePreviewData {
  episodeName: string;
  shots: { index: number; duration: number; videoURL: string }[];
  expiresAt: string;
}

// Usage Summary (admin)
export interface UsageSummaryData {
  llmTotalTokens: number;
  llmCost: number;
  llmCallCount: number;
  imageCount: number;
  imageCost: number;
  videoCount: number;
  videoDuration: number;
  videoCost: number;
  serviceCost: number;
}

export interface ProjectUsageItem {
  projectId: string;
  projectName: string;
  ownerName: string;
  createdAt: string;
  llmTokens: number;
  llmCost: number;
  imageCount: number;
  imageCost: number;
  videoCount: number;
  videoDuration: number;
  videoCost: number;
  serviceCost: number;
  totalCost: number;
  totalCredits: number;
}

export interface ProjectUsageListResponse {
  items: ProjectUsageItem[];
  total: number;
}

export const getUsageSummary = (): Promise<UsageSummaryData> =>
  request('/admin/usage-summary');

export const getUsageByProject = (page: number = 1, pageSize: number = 10, search?: string, owner?: string, exportMode?: boolean, projectId?: string, sort?: 'created_at' | 'total_cost'): Promise<ProjectUsageListResponse> =>
  request(`/admin/usage-by-project?page=${page}&pageSize=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}${owner ? `&owner=${encodeURIComponent(owner)}` : ''}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}${sort ? `&sort=${encodeURIComponent(sort)}` : ''}${exportMode ? '&export=true' : ''}`);

export interface AccountUsageRow {
  userId: string;
  username: string;
  credits: number;
  convertedFee: number;
  cost: number;
  grossMargin: number;
}

export interface AccountUsageGroupBlock {
  groupId: string;
  groupName: string;
  subtotal: AccountUsageRow;
  members: AccountUsageRow[];
}

export interface AccountUsageResponse {
  month: string;
  availableMonths: string[];
  groups: AccountUsageGroupBlock[];
  ungrouped: AccountUsageRow[];
  unknown: { cost: number };
}

export interface AccountUsageDetailItem {
  projectId: string;
  projectName: string;
  credits: number;
  convertedFee: number;
  cost: number;
}

export interface AccountGroupWithCount {
  id: string;
  name: string;
  memberCount: number;
}

export const getUsageByAccount = (month?: string, search?: string): Promise<AccountUsageResponse> =>
  request(`/admin/usage-by-account?${month ? `month=${month}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`);

export const getAccountUsageDetail = (userId: string, month?: string): Promise<{ userId: string; month: string; items: AccountUsageDetailItem[] }> =>
  request(`/admin/usage-by-account/${encodeURIComponent(userId)}?${month ? `month=${month}` : ''}`);

export interface AccountEpisodeItem {
  episodeId: string;
  episodeName: string;
  credits: number;
  convertedFee: number;
  cost: number;
  grossMargin: number;
}

export interface AccountProjectEpisodesResponse {
  userId: string;
  projectId: string;
  projectName: string;
  month: string;
  episodes: AccountEpisodeItem[];
  aggregate: AccountEpisodeItem; // episodeId="" 综合(不分集)
}

export const getAccountProjectEpisodes = (
  userId: string,
  projectId: string,
  month?: string,
): Promise<AccountProjectEpisodesResponse> =>
  request(
    `/admin/usage-by-account/${encodeURIComponent(userId)}/projects/${encodeURIComponent(projectId)}/episodes?${month ? `month=${month}` : ''}`,
  );

export const listAccountGroups = (): Promise<{ groups: AccountGroupWithCount[] }> =>
  request('/admin/account-groups');

export const createAccountGroup = (name: string): Promise<AccountGroupWithCount> =>
  request('/admin/account-groups', { method: 'POST', body: JSON.stringify({ name }) });

export const assignAccountGroupMember = (groupId: string, username: string): Promise<{ message: string }> =>
  request(`/admin/account-groups/${encodeURIComponent(groupId)}/members`, { method: 'POST', body: JSON.stringify({ username }) });

export const deleteAccountGroup = (groupId: string): Promise<{ message: string }> =>
  request(`/admin/account-groups/${encodeURIComponent(groupId)}`, { method: 'DELETE' });

export interface AccountGroupMember {
  id: string;
  username: string;
  phone: string | null;
  createdAt: string;
}

export const listAccountGroupMembers = (groupId: string): Promise<{ members: AccountGroupMember[] }> =>
  request(`/admin/account-groups/${encodeURIComponent(groupId)}/members`);

// CSV 导出走后端接口;返回相对路径,页面用 getToken() 拼鉴权头 fetch 下载。
// groupId 为空导出全部;指定时只导出该账号组。
export const exportAccountUsageUrl = (month: string, search?: string, groupId?: string): string =>
  `/admin/usage-by-account/export?month=${month}${search ? `&search=${encodeURIComponent(search)}` : ''}${groupId ? `&groupId=${encodeURIComponent(groupId)}` : ''}`;

export interface BackfillOwnerReport {
  tables: { table: string; total: number; audit: number; current: number; unknown: number }[];
}

// 远程触发历史 owner 回填(幂等,只补 owner_id 为空的存量行)。
export const backfillUsageOwner = (): Promise<BackfillOwnerReport> =>
  request('/admin/usage-by-account/backfill-owner', { method: 'POST' });

// Style Tests
export const listStyleTests = (projectId: string, elementType: string, elementName: string): Promise<AssetStyleTest[]> =>
  request(`/projects/${projectId}/style-tests?elementType=${encodeURIComponent(elementType)}&elementName=${encodeURIComponent(elementName)}`);

export const createStyleTest = (projectId: string, data: { elementType: string; elementName: string; templateId: string }): Promise<AssetStyleTest> =>
  request(`/projects/${projectId}/style-tests`, { method: 'POST', body: JSON.stringify(data) });

export const deleteStyleTest = (projectId: string, testId: string): Promise<void> =>
  request(`/projects/${projectId}/style-tests/${testId}`, { method: 'DELETE' });

export const uploadStyleTestRefImage = async (projectId: string, testId: string, file: File): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/style-tests/${testId}/ref-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '上传失败' }));
    throw new Error(localizeError(err.error || '上传失败'));
  }
  return res.json();
};

export const deleteStyleTestRefImage = (projectId: string, testId: string, url: string): Promise<void> =>
  request(`/projects/${projectId}/style-tests/${testId}/ref-image`, { method: 'DELETE', body: JSON.stringify({ url }) });

export const importStyleTestRefURL = (projectId: string, testId: string, urls: string[]): Promise<void> =>
  request(`/projects/${projectId}/style-tests/${testId}/import-ref-url`, { method: 'POST', body: JSON.stringify({ urls }) });

export const generateStyleTestImage = (projectId: string, testId: string, prompt: string): Promise<{ taskId: string }> =>
  request(`/projects/${projectId}/style-tests/${testId}/generate`, { method: 'POST', body: JSON.stringify({ prompt }) });

export const applyStyleTest = (projectId: string, testId: string, imageUrl: string): Promise<void> =>
  request(`/projects/${projectId}/style-tests/${testId}/apply`, { method: 'POST', body: JSON.stringify({ imageUrl }) });

// 获取 TOS 预签名下载 URL（绕过 CORS/PNA 限制，保留自定义文件名）
export const getDownloadUrl = (url: string, filename: string): Promise<{ downloadUrl: string }> =>
  request(`/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`);

export const getSharePreview = (code: string): Promise<SharePreviewData> => {
  // 公开接口，不需要 token，直接用 fetch
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';
  return fetch(`${API_BASE}/share/${encodeURIComponent(code)}`).then(async res => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(localizeError(err.error || 'Request failed'));
    }
    return res.json();
  });
};

// ==================== 资产禁用/启用 ====================

export const disableAsset = (projectId: string, category: number, name: string, confirmed: boolean): Promise<{ affectedShotCount?: number; needConfirmation?: boolean; affectedDetails?: Array<{ episodeIndex: number; shotIndex: number }>; disabled?: boolean; removedRefCount?: number; cancelledTaskCount?: number }> =>
  request(`/projects/${projectId}/disable-asset`, {
    method: 'POST',
    body: JSON.stringify({ category, name, confirmed }),
  });

export const enableAsset = (projectId: string, category: number, name: string): Promise<{ enabled: boolean }> =>
  request(`/projects/${projectId}/enable-asset`, {
    method: 'POST',
    body: JSON.stringify({ category, name }),
  });

// 资产审核 API
export interface AssetReviewItem {
  characterName: string;
  characterState: string;
  imageUrl: string;
  status: string; // unsubmitted / pending / processing / active / failed
  errorMsg?: string;
  assetId?: string;
}

export const submitAssetReview = (episodeId: string): Promise<{ submitted: number; skipped: number; errors?: string[] }> =>
  request(`/episodes/${episodeId}/asset-review/submit`, { method: 'POST' });

export const getAssetReviewStatus = (episodeId: string): Promise<{ required: boolean; allActive: boolean; reviews: AssetReviewItem[] }> =>
  request(`/episodes/${episodeId}/asset-review/status`);

export const getProjectAssetReviews = (projectId: string): Promise<{ reviews: AssetReviewItem[] }> =>
  request(`/projects/${projectId}/asset-reviews`);

// Resource Review (分镜资源审核)
export const getResourceReviewStatus = (episodeId: string): Promise<{ required: boolean; allActive: boolean; reviews: ResourceReviewItem[] }> =>
  request(`/episodes/${episodeId}/resource-review/status`);

export const submitResourceReview = (episodeId: string): Promise<{ submitted: number; skipped: number; errors?: string[] }> =>
  request(`/episodes/${episodeId}/resource-review/submit`, { method: 'POST' });

export const getProjectResourceReviews = (projectId: string, status?: string): Promise<{ reviews: ResourceReviewItem[] }> =>
  request(`/projects/${projectId}/resource-reviews${status ? `?status=${status}` : ''}`);

// Credits - User
export const getCreditsBalance = (): Promise<{ credits: number }> =>
  request('/credits/balance');

export const getMyRecharges = (page: number = 1, pageSize: number = 20): Promise<{ items: CreditRecharge[]; total: number }> =>
  request(`/credits/recharges?page=${page}&pageSize=${pageSize}`);

export const getMyConsumptions = (page: number = 1, pageSize: number = 20): Promise<{ items: CreditConsumption[]; total: number }> =>
  request(`/credits/consumptions?page=${page}&pageSize=${pageSize}`);

// Credits - Project
export const getProjectCreditsSummary = (projectId: string): Promise<ProjectCreditsSummary[]> =>
  request(`/projects/${projectId}/credits/summary`);

export const getProjectCreditsConsumptions = (projectId: string, page: number = 1, pageSize: number = 20): Promise<{ items: CreditConsumption[]; total: number }> =>
  request(`/projects/${projectId}/credits/consumptions?page=${page}&pageSize=${pageSize}`);

// Credits - Admin
export const adminRechargeCredits = (userId: string, amount: number, remark: string): Promise<CreditRecharge> =>
  request(`/admin/users/${userId}/credits`, { method: 'POST', body: JSON.stringify({ amount, remark }) });

export const adminDeductCredits = (userId: string, amount: number, remark: string, allowNegative: boolean = false): Promise<CreditRecharge> =>
  request(`/admin/users/${userId}/credits/deduct`, { method: 'POST', body: JSON.stringify({ amount, remark, allowNegative }) });

export const adminGetRecharges = (userId: string, page: number = 1, pageSize: number = 20): Promise<{ items: CreditRecharge[]; total: number }> =>
  request(`/admin/users/${userId}/credits/recharges?page=${page}&pageSize=${pageSize}`);

export const adminGetConsumptions = (userId: string, page: number = 1, pageSize: number = 20): Promise<{ items: CreditConsumption[]; total: number }> =>
  request(`/admin/users/${userId}/credits/consumptions?page=${page}&pageSize=${pageSize}`);

// Recharge Requests (充值申请)
export const createRechargeRequest = (amount: number, remark: string): Promise<RechargeRequest> =>
  request('/credits/recharge-requests', { method: 'POST', body: JSON.stringify({ amount, remark }) });

export const listMyRechargeRequests = (page: number = 1, pageSize: number = 20): Promise<{ items: RechargeRequest[]; total: number }> =>
  request(`/credits/recharge-requests?page=${page}&pageSize=${pageSize}`);

export const cancelRechargeRequest = (id: string): Promise<{ success: boolean }> =>
  request(`/credits/recharge-requests/${id}/cancel`, { method: 'POST' });

export const adminListRechargeRequests = (status: 'pending' | 'reviewed' | 'all' = 'pending', page: number = 1, pageSize: number = 20): Promise<{ items: RechargeRequest[]; total: number }> =>
  request(`/admin/recharge-requests?status=${status}&page=${page}&pageSize=${pageSize}`);

export const adminApproveRechargeRequest = (id: string, amount: number, remark: string): Promise<RechargeRequest> =>
  request(`/admin/recharge-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ amount, remark }) });

export const adminRejectRechargeRequest = (id: string, reason: string): Promise<RechargeRequest> =>
  request(`/admin/recharge-requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });

// Platform Reconciliation (admin)
export interface PlatformReconciliationVideo {
  platform: string;
  model: string;
  count: number;
  duration: number;
  cost: number;
  credits: number;
}

export interface PlatformReconciliationImage {
  platform: string;
  model: string;
  count: number;
  cost: number;
  credits: number;
}

export interface PlatformReconciliationLLM {
  provider: string;
  model: string;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  credits: number;
}

export interface PlatformReconciliationService {
  name: string;
  count: number;
  cost: number;
  credits: number;
}

export interface PlatformReconciliationData {
  month: string;
  availableMonths: string[];
  video: PlatformReconciliationVideo[] | null;
  image: PlatformReconciliationImage[] | null;
  llm: PlatformReconciliationLLM[] | null;
  service: PlatformReconciliationService[] | null;
  totalCost: number;
}

export const getPlatformReconciliation = (month?: string): Promise<PlatformReconciliationData> =>
  request(`/admin/platform-reconciliation${month ? `?month=${month}` : ''}`);

export const refreshPlatformReconciliation = (): Promise<{ message: string }> =>
  request('/admin/platform-reconciliation/refresh', { method: 'POST' });

export const rebuildPlatformReconciliation = (): Promise<{ message: string }> =>
  request('/admin/platform-reconciliation/rebuild', { method: 'POST' });

// Platform Reconciliation — by date range (raw _usages aggregation, no monthly table)
export interface PlatformReconciliationRangeData {
  start: string;
  end: string;
  days: number;
  video: PlatformReconciliationVideo[] | null;
  image: PlatformReconciliationImage[] | null;
  llm: PlatformReconciliationLLM[] | null;
  service: PlatformReconciliationService[] | null;
  totalCost: number;
}

export const getPlatformReconciliationByRange = (
  start: string,
  end: string
): Promise<PlatformReconciliationRangeData> =>
  request(
    `/admin/platform-reconciliation/range?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
  );

// Service Pricing (public - enabled only)
export const listServicePricingPublic = (): Promise<ServicePricing[]> =>
  request('/service-pricing');

// Service Pricing (admin)
export const listServicePricing = (): Promise<ServicePricing[]> =>
  request('/admin/service-pricing');

export const updateServicePricing = (serviceType: string, data: Partial<ServicePricing>): Promise<ServicePricing> =>
  request(`/admin/service-pricing/${serviceType}`, { method: 'PUT', body: JSON.stringify(data) });

export const getServiceUsageSummary = (): Promise<ServiceUsageSummary[]> =>
  request('/admin/service-usage');

// Character Voice
export const uploadCharacterVoice = async (projectId: string, characterName: string, file: File): Promise<{ voiceUrl: string; voiceSource: string; voiceFileName: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const qs = new URLSearchParams({ characterName }).toString();
  const res = await fetch(`${API_BASE}/projects/${projectId}/character-voice?${qs}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: '上传失败' }));
    throw new Error(localizeError(err.error || '上传失败'));
  }
  return res.json();
};

export const applyCharacterVoice = (projectId: string, characterName: string, audioUrl: string, source: string, extractInfo?: string): Promise<{ voiceUrl: string; voiceSource: string }> =>
  request(`/projects/${projectId}/character-voice?${new URLSearchParams({ characterName }).toString()}`, {
    method: 'PUT',
    body: JSON.stringify({ audioUrl, source, extractInfo }),
  });

// Voice Extraction
export const getExtractedVersionIds = (episodeId: string): Promise<{ versionIds: string[] }> =>
  request(`/episodes/${episodeId}/extracted-versions`);

export interface ActiveExtraction {
  versionId: string;
  taskId: string;
  status: string;
}

export const getActiveExtractions = (episodeId: string): Promise<{ tasks: ActiveExtraction[] }> =>
  request(`/episodes/${episodeId}/active-extractions`);

export const extractVoices = (shotId: string, versionId: string): Promise<VoiceExtractionTask> =>
  request(`/shots/${shotId}/versions/${versionId}/extract-voices`, { method: 'POST' });

export const getVoiceExtraction = (taskId: string): Promise<VoiceExtractionTask> =>
  request(`/voice-extractions/${taskId}`);

export interface SwapVersionStatus {
  versionId: string;
  taskId: string;
  status: string;
  errorMessage?: string;
  replacedVocalUrl?: string;
  bgmUrl?: string;
  matchedSegments?: VoiceSwapMatchedSegment[];
}

export const getSwapVersionStatuses = (episodeId: string): Promise<{ statuses: SwapVersionStatus[] }> =>
  request(`/episodes/${episodeId}/swap-statuses`);

// Voice Swap
export const swapVoice = (shotId: string, versionId: string, characters: string[]): Promise<VoiceSwapTask> =>
  request(`/shots/${shotId}/versions/${versionId}/swap-voice`, {
    method: 'POST',
    body: JSON.stringify({ characters }),
  });

export const getVoiceSwap = (taskId: string): Promise<VoiceSwapTask> =>
  request(`/voice-swaps/${taskId}`);

// Subtitle Erase
export const submitSubtitleErase = (versionId: string): Promise<{ data: SubtitleEraseTask }> =>
  request(`/versions/${versionId}/subtitle-erase`, { method: 'POST' });

export const getSubtitleEraseStatus = (versionId: string): Promise<{ data: SubtitleEraseTask | null }> =>
  request(`/versions/${versionId}/subtitle-erase`);

export const batchSubtitleEraseStatus = (versionIds: string[]): Promise<{ data: Record<string, SubtitleEraseTask> }> =>
  request(`/subtitle-erase/batch-status`, { method: 'POST', body: JSON.stringify({ versionIds }) });

// ── 公告 (Announcements) ──────────────────────

// 用户端
export const listAnnouncements = (page = 1, pageSize = 20): Promise<{ items: Announcement[]; total: number; page: number }> =>
  request(`/announcements?page=${page}&pageSize=${pageSize}`);

export const getAnnouncement = (id: string): Promise<Announcement> =>
  request(`/announcements/${id}`);

export const markAnnouncementRead = (id: string): Promise<void> =>
  request(`/announcements/${id}/read`, { method: 'POST' });

// 管理员端
export const adminListAnnouncements = (page = 1, pageSize = 20): Promise<{ items: Announcement[]; total: number; page: number }> =>
  request(`/admin/announcements?page=${page}&pageSize=${pageSize}`);

export const adminCreateAnnouncement = (data: { title: string; content?: string; type?: string; priority?: string }): Promise<Announcement> =>
  request('/admin/announcements', { method: 'POST', body: JSON.stringify(data) });

export const adminUpdateAnnouncement = (id: string, data: Partial<{ title: string; content: string; type: string; priority: string }>): Promise<Announcement> =>
  request(`/admin/announcements/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const adminPublishAnnouncement = (id: string): Promise<Announcement> =>
  request(`/admin/announcements/${id}/publish`, { method: 'POST' });

export const adminArchiveAnnouncement = (id: string): Promise<void> =>
  request(`/admin/announcements/${id}/archive`, { method: 'POST' });

export const adminDeleteAnnouncement = (id: string): Promise<void> =>
  request(`/admin/announcements/${id}`, { method: 'DELETE' });

// ── 通知 (Notifications) ──────────────────────

export const listNotifications = (page = 1, pageSize = 20): Promise<{ items: Notification[]; total: number; page: number }> =>
  request(`/notifications?page=${page}&pageSize=${pageSize}`);

export const markNotificationRead = (id: string): Promise<void> =>
  request(`/notifications/${id}/read`, { method: 'POST' });

export const markAllNotificationsRead = (): Promise<{ count: number }> =>
  request('/notifications/read-all', { method: 'POST' });

export const markAllAnnouncementsRead = (): Promise<{ count: number }> =>
  request('/announcements/read-all', { method: 'POST' });

// ── 未读汇总 ──────────────────────────────────

export const getUnreadSummary = (): Promise<UnreadSummary> =>
  request('/messages/unread-summary');

// ── 服务日志（管理员可用）──────────────────────

export const getServiceLogs = (
  page: number = 1,
  pageSize: number = 20,
  level?: string,
  category?: string,
): Promise<{ data: ServiceLog[]; total: number; page: number; pageSize: number }> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (level) params.set('level', level);
  if (category) params.set('category', category);
  return request(`/admin/service-logs?${params.toString()}`);
};

export * from './stateStylingSearch';

// ─── Showcase ────────────────────────────────────────────────
export const getPublicShowcase = (language: string): Promise<{ items: ShowcaseItem[] }> =>
  request(`/showcase?language=${encodeURIComponent(language)}`);

export const adminListShowcase = (language?: string, isPublished?: boolean): Promise<{ items: ShowcaseItem[] }> => {
  const params = new URLSearchParams();
  if (language) params.set('language', language);
  if (isPublished !== undefined) params.set('is_published', String(isPublished));
  const qs = params.toString();
  return request(`/admin/showcase${qs ? '?' + qs : ''}`);
};

export const adminCreateShowcase = (data: {
  title: string; description: string; thumbnailUrl: string;
  videoUrl: string; language: string; sortOrder: number; isPublished: boolean;
}): Promise<{ item: ShowcaseItem }> =>
  request('/admin/showcase', { method: 'POST', body: JSON.stringify(data) });

export const adminUpdateShowcase = (id: number, data: Partial<{
  title: string; description: string; thumbnailUrl: string;
  videoUrl: string; language: string; sortOrder: number; isPublished: boolean;
}>): Promise<{ item: ShowcaseItem }> =>
  request(`/admin/showcase/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const adminDeleteShowcase = (id: number): Promise<{ success: boolean }> =>
  request(`/admin/showcase/${id}`, { method: 'DELETE' });

export const uploadShowcaseThumbnail = async (file: File): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/admin/showcase/upload-thumbnail`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(localizeError(err.error || 'Upload failed'));
  }
  return res.json();
};

export const uploadShowcaseVideo = async (file: File): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const res = await fetch(`${API_BASE}/admin/showcase/upload-video`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(localizeError(err.error || 'Upload failed'));
  }
  return res.json();
};

export const uploadCommentImage = async (file: File): Promise<import('../types').CommentImage> => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_BASE}/comment-images`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: '上传失败' }));
    throw new Error(err.error || '上传失败');
  }
  return response.json();
};
